import { CommonModule } from "@angular/common";
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, inject } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NavigationStart, Router } from "@angular/router";
import { Subscription, filter } from "rxjs";
import { VFIconComponent } from "../../../shared/component/vficon/vficon.component";
import {
  NotificationItem,
  NotificationSeverity,
  NotificationType,
  NotificationsService,
} from "../../../shared/services/notifications.service";

type BellView = "all" | "unread" | "high";

@Component({
  selector: "app-user-notification-dropdown",
  standalone: true,
  imports: [CommonModule, FormsModule, VFIconComponent],
  templateUrl: "./user-notification-dropdown.component.html",
  styleUrls: ["./user-notification-dropdown.component.scss"],
})
export class UserNotificationDropdownComponent implements OnInit, OnDestroy {
  private static readonly USER_FETCH_LIMIT: number = 50;
  private static readonly USER_FETCH_DAYS_BACK: number = 30;
  private static readonly MAX_VISIBLE_NOTIFICATIONS: number = 10;
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly router: Router = inject(Router);
  private readonly refreshIntervalMs: number = 15_000;
  private readonly pendingReadNotificationIds: Set<string> = new Set<string>();
  private isRefreshingFromApi: boolean = false;
  private isFlushingPendingReads: boolean = false;
  public isMarkingAllAsRead: boolean = false;
  private autoRefreshHandle: ReturnType<typeof setInterval> | null = null;
  private navigationSubscription: Subscription | null = null;

  @ViewChild("dropdownRoot", { static: true })
  private dropdownRootRef!: ElementRef<HTMLElement>;

  @Input() public currentTenant: string | null = null;
  @Input() public isLoggedIn: boolean = false;
  @Input() public currentUserEmail: string | null = null;

  public activeBellView: BellView = "all";
  public bellSearchTerm: string = "";
  public bellFilterType: NotificationType | "all" = "all";
  public bellFilterSeverity: NotificationSeverity | "all" = "all";
  public bellFilterCloudProvider: string = "all";

  public async ngOnInit(): Promise<void> {
    if (!this.isLoggedIn) {
      return;
    }

    this.navigationSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationStart))
      .subscribe(() => {
        void this.flushPendingReadNotifications();
      });

    this.dropdownRootRef.nativeElement.addEventListener("hidden.bs.dropdown", this.handleDropdownHidden);
    await this.refreshUserNotifications();
    this.startAutoRefresh();
  }

  public ngOnDestroy(): void {
    this.stopAutoRefresh();
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.dropdownRootRef.nativeElement.removeEventListener("hidden.bs.dropdown", this.handleDropdownHidden);
    this.navigationSubscription?.unsubscribe();
    void this.flushPendingReadNotifications();
  }

  public async refreshUserNotifications(): Promise<void> {
    if (!this.isLoggedIn || this.isRefreshingFromApi) {
      return;
    }

    this.isRefreshingFromApi = true;

    try {
      await this.notificationsService.refreshNotificationsFromUserApi({
        limit: UserNotificationDropdownComponent.USER_FETCH_LIMIT,
        daysBack: UserNotificationDropdownComponent.USER_FETCH_DAYS_BACK,
        includeRead: true,
      });
    } finally {
      this.isRefreshingFromApi = false;
    }
  }

  public async handleBellToggleClick(): Promise<void> {
    const unreadCountAtClick: number = this.bellUnreadCount;
    this.activeBellView = unreadCountAtClick > 0 ? "unread" : "all";
    await this.refreshUserNotifications();
  }

  public get bellNotifications(): NotificationItem[] {
    return this.notificationsService.getNotificationsForAudience(
      this.currentTenant,
      this.getCurrentAudiencePath(),
      this.currentUserEmail,
    );
  }

  public get bellUnreadCount(): number {
    return this.notificationsService.getUnreadCountForAudience(
      this.currentTenant,
      this.getCurrentAudiencePath(),
      this.currentUserEmail,
    );
  }

  public get bellHighCount(): number {
    return this.bellNotifications.filter((notification) => notification.priority === "high").length;
  }

  public get filteredBellNotifications(): NotificationItem[] {
    let notifications: NotificationItem[] = this.bellNotifications.filter((notification) => {
      if (this.activeBellView === "unread") {
        return notification.unread;
      }

      if (this.activeBellView === "high") {
        return notification.priority === "high";
      }

      return true;
    });

    if (this.bellSearchTerm.trim()) {
      const term: string = this.bellSearchTerm.toLowerCase();
      notifications = notifications.filter(
        (notification) =>
          notification.headline.toLowerCase().includes(term) || notification.description.toLowerCase().includes(term),
      );
    }

    if (this.bellFilterType !== "all") {
      notifications = notifications.filter((notification) => notification.type === this.bellFilterType);
    }

    if (this.bellFilterSeverity !== "all") {
      notifications = notifications.filter((notification) => notification.severity === this.bellFilterSeverity);
    }

    if (this.bellFilterCloudProvider !== "all") {
      notifications = notifications.filter((notification) =>
        notification.cloud_provider.includes(this.bellFilterCloudProvider),
      );
    }

    return notifications.slice(0, UserNotificationDropdownComponent.MAX_VISIBLE_NOTIFICATIONS);
  }

  public setBellView(view: BellView): void {
    this.activeBellView = view;
  }

  public async markAllAsRead($event: Event): Promise<void> {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.isMarkingAllAsRead) {
      return;
    }

    const unreadNotifications: NotificationItem[] = this.bellNotifications.filter(
      (notification) => notification.unread,
    );
    if (unreadNotifications.length === 0) {
      return;
    }

    this.isMarkingAllAsRead = true;

    try {
      const results: Array<boolean> = await Promise.all(
        unreadNotifications.map((notification) => this.notificationsService.markNotification(notification.id, "read")),
      );

      const allMarked: boolean = results.every((result) => result);
      if (allMarked) {
        this.pendingReadNotificationIds.clear();
      }
    } finally {
      this.isMarkingAllAsRead = false;
    }
  }

  public onNotificationClick(notification: NotificationItem, $event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (notification.unread) {
      this.pendingReadNotificationIds.add(notification.id);
    }
  }

  public resetBellFilters(): void {
    this.bellSearchTerm = "";
    this.bellFilterType = "all";
    this.bellFilterSeverity = "all";
    this.bellFilterCloudProvider = "all";
  }

  public getAvailableFilterTypes(): NotificationType[] {
    return this.getUniqueValues(this.bellNotifications.map((notification) => notification.type));
  }

  public getAvailableFilterSeverities(): NotificationSeverity[] {
    return this.getUniqueValues(this.bellNotifications.map((notification) => notification.severity));
  }

  public getAvailableFilterCloudProviders(): string[] {
    return this.getUniqueValues(this.bellNotifications.flatMap((notification) => notification.cloud_provider));
  }

  public getNotificationTypeLabel(type: NotificationType): string {
    return this.notificationsService.getTypeLabel(type);
  }

  private getCurrentAudiencePath(): string {
    if (typeof window === "undefined") {
      return this.router.url;
    }

    const normalizedHash: string = window.location.hash.replace(/^#/, "").trim();
    if (window.location.pathname === "/dashboard" && normalizedHash.startsWith("vcloudsmart/")) {
      return `/${normalizedHash}`;
    }

    return this.router.url;
  }

  private getUniqueValues<T extends string>(values: T[]): T[] {
    return Array.from(new Set(values)).sort();
  }

  private startAutoRefresh(): void {
    if (this.autoRefreshHandle) {
      return;
    }
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.autoRefreshHandle = setInterval(() => {
      void this.refreshUserNotifications();
    }, this.refreshIntervalMs);
  }

  private stopAutoRefresh(): void {
    if (!this.autoRefreshHandle) {
      return;
    }

    clearInterval(this.autoRefreshHandle);
    this.autoRefreshHandle = null;
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === "visible") {
      void this.refreshUserNotifications();
    }
  };

  private readonly handleDropdownHidden = (): void => {
    void this.flushPendingReadNotifications();
  };

  private async flushPendingReadNotifications(): Promise<void> {
    if (this.isFlushingPendingReads || this.pendingReadNotificationIds.size === 0) {
      return;
    }

    this.isFlushingPendingReads = true;
    const notificationIds: string[] = Array.from(this.pendingReadNotificationIds);

    try {
      const results: boolean[] = await Promise.all(
        notificationIds.map((notificationId) => this.notificationsService.markNotification(notificationId, "read")),
      );

      for (let index = 0; index < notificationIds.length; index += 1) {
        if (results[index]) {
          this.pendingReadNotificationIds.delete(notificationIds[index]);
        }
      }
    } finally {
      this.isFlushingPendingReads = false;
    }
  }
}
